import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, exhaustMap, filter, map, mergeMap, of, switchMap, withLatestFrom } from "rxjs";
import { UserActions, UserRoleActions } from "../actions";

import { Injectable } from "@angular/core";
import { entityRoutePaths } from "@app/entity/entity.routes";
import { DataActions } from "@app/project/store/actions";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { SendEmailDatabaseService } from "@app/shared/databases/email.database";
import { ActionWord } from "@app/shared/resources/action-word";
import { rekeyDbResultArray } from "@app/shared/resources/resource-objectifier";
import { UserHelper } from "@app/user/model/user";
import { composeStaffEmail } from "@app/user/resources/user-invite-email";
import { UserDbService } from "@app/user/service/user-db.service";
import { UserRoleService } from "@app/user/service/user-role.service";
import { Store, select } from "@ngrx/store";
import { UserManagementSelectors } from "..";

const dashboardProduct = {
    name: "FusionPro",
    url: "https://dealerdev.clarecontrols.com",
};

@Injectable({
    providedIn: "root",
})
export class UserManagementEffects {
    effectsUtils = new EffectsUtils(this.actions$);

    constructor(
        private readonly actions$: Actions,
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly userDbService: UserDbService,
        private readonly userRoleService: UserRoleService,
        // private readonly userManegement: UserManagementService,
        readonly store: Store,
        private readonly emailDbService: SendEmailDatabaseService
    ) {}

    createUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.create),
            // email address check
            exhaustMap(({ user, callbackEmitter, logData }) =>
                this.userDbService.checkForEmailExistence(user).pipe(
                    exhaustMap(() =>
                        DataActions.toAction2<UserActions.ActionResult, UserActions.ActionPayload>()(
                            this.userDbService.createUser({ user }, logData),
                            UserActions.createSuccess,
                            UserActions.createFailure,
                            callbackEmitter
                        )
                    ),
                    catchError(DataActions.toError2(UserActions.createFailure))
                )
            ),
            catchError(DataActions.toError2(UserActions.createFailure))
        )
    );

    loadEntityUsers$ = this.effectsUtils.createExhaustEffect({
        dbMethod: action => this.userDbService.getEntityUsers$(action),
        actionSet: UserActions.loadEntityUsersSet,
    });

    loadAllEntityUsers$ = this.effectsUtils.createExhaustEffect({
        dbMethod: action => {
            const getUsers$ = action?.entityId
                ? this.userDbService.getEntityAllStaffs({ entityId: action.entityId })
                : this.userDbService.getAllUsers();
            return getUsers$.pipe(map(users => ({ users: rekeyDbResultArray(new UserHelper())(users) })));
        },
        actionSet: UserActions.loadAllEntityUsersSet,
    });

    loadAllEntityCustomers$ = this.effectsUtils.createExhaustEffect({
        dbMethod: action => {
            const getUsers$ = action?.entityId
                ? this.userDbService.getEntityAllCustomers({ entityId: action.entityId })
                : this.userDbService
                      .getAllUsers()
                      .pipe(withLatestFrom(this.store.pipe(select(UserManagementSelectors.getCustomerRoleId)).pipe(filter(data => !!data))))
                      .pipe(
                          map(([users, custommerRoleId]) => (users || []).filter(user => user.role_id === custommerRoleId && user.id !== 0))
                      );

            return getUsers$.pipe(map(users => ({ users: rekeyDbResultArray(new UserHelper())(users) })));
        },
        actionSet: UserActions.loadAllEntityCustomersSet,
    });

    sendUserWelcomeEmail$ = this.effectsUtils.createExhaustEffect({
        dbMethod: action => {
            const subject = `You have been invited to collaborate in ${dashboardProduct.name}`;
            const body = composeStaffEmail(
                `${window.location.protocol}//${window.location.host}/signup?staff=true&email=${encodeURIComponent(
                    action.recipientEmail
                )}&firstName=${encodeURIComponent(action.recipientFirstName)}&lastName=${encodeURIComponent(
                    action.recipientLastName
                )}&inviteCode=${action.inviteCode}`
            );
            return this.emailDbService.sendEmailRequest$(
                action.recipientEmail,
                action.recipientFirstName + " " + action.recipientLastName,
                subject,
                body
            );
        },
        actionSet: UserActions.sendUserWelcomeEmailSet,
    });

    sendUserWelcomeEmailSuccess$ = this.effectsUtils.createSuccessEffect(
        UserActions.sendUserWelcomeEmailSuccess,
        ActionWord.sent,
        "email invite"
    );

    sendUserWelcomeEmailFailure$ = this.effectsUtils.createFailureEffect(
        UserActions.sendUserWelcomeEmailFailure,
        ActionWord.sent,
        "email invite"
    );

    loadUser$ = this.effectsUtils.createSwitchEffect({
        dbMethod: ({ userId }) => this.userDbService.getUser({ userId }),
        actionSet: UserActions.loadSingleSet,
    });

    loadActiveProjectUser$ = this.effectsUtils.createSwitchEffect({
        dbMethod: ({ userId }) => this.userDbService.getUser({ userId }),
        actionSet: UserActions.loadActiveProjectUserSet,
    });

    updateUser$ = this.effectsUtils.createExhaustEffect({
        dbMethod: (payload: UserActions.ActionPayload) => this.userDbService.updateUser({ user: payload.user }, payload.logData),
        actionSet: UserActions.updateSet,
    });

    updateUserSuccess$ = this.effectsUtils.createSuccessEffect(
        UserActions.updateSuccess,
        ActionWord.saved,
        this.userDbService.helper.dbTypeName
    );

    updateUserFailure$ = this.effectsUtils.createFailureEffect(
        UserActions.updateFailure,
        ActionWord.saved,
        this.userDbService.helper.dbTypeName
    );

    deleteUser$ = this.effectsUtils.createExhaustEffect({
        dbMethod: (payload: UserActions.ActionPayload) => {
            console.log(payload);
            return this.userDbService.deleteUser({ user: payload.user }, payload.logData);
        },
        actionSet: UserActions.deleteSet,
    });

    deleteUserConfirmation$ = this.effectsUtils.createDeleteConfirmationEffect({
        actionSet: UserActions.deleteConfirmationSet,
        dbMethod: ({ user, logData }) => of({ payload: { user, logData }, display: user?.email || null }),
        confirmationService: this.deleteConfirmationService,
    });

    createUserSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.createSuccess),
            mergeMap(({ user }) => [
                UserActions.sendUserWelcomeEmail({
                    recipientEmail: user.email,
                    recipientFirstName: user.firstName,
                    recipientLastName: user.lastName,
                    inviteCode: user.inviteCode,
                }),
                DataActions.handleSuccessMessage2(ActionWord.created, null, this.userDbService.helper.dbTypeName)(),
            ])
        )
    );
    createUserFailure$ = this.effectsUtils.createFailureEffect(UserActions.createFailure, ActionWord.saved);

    deleteUserSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.deleteSuccess),
            map(({ user }) =>
                DataActions.handleSuccessMessage2(
                    ActionWord.deleted,
                    `${entityRoutePaths.root}${entityRoutePaths.edit}/${user.entityId}/staff`,
                    this.userDbService.helper.dbTypeName
                )()
            )
        )
    );
    deleteUserFailure$ = this.effectsUtils.createFailureEffect(UserActions.deleteFailure, ActionWord.deleted);

    loadUserRoles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserRoleActions.load),
            switchMap(({ authUser }) =>
                this.userRoleService.getUserRoles(authUser).pipe(
                    mergeMap(userRoles => {
                        const customerRole = this.userRoleService.getCustomerRole(userRoles);
                        return [UserRoleActions.setCustomerRoleId({ customerRole }), UserRoleActions.loadSuccess({ userRoles })];
                    })
                )
            ),
            catchError(DataActions.toError2(UserRoleActions.loadFailure))
        )
    );
}
